import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip, Button } from '@bufferapp/ui';
import FlashIcon from '@bufferapp/ui/Icon/Icons/Flash';
import ConfirmDeleteButton from '~/shared-components/ConfirmDeleteButton';
import { PostAction } from '~/post/types';
import { ThreadsFreeOBPaywall } from '~/composer/composer/components/ThreadsFreeOBPaywall';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { useAccount } from '~/accountContext';

import {
  SingleActionButton,
  StackedActionButton,
  ButtonWrapper,
} from './styles';

export type PostFooterButtonsProps = {
  isLoading?: boolean;
  singleActions: PostAction[];
  stackedActions: PostAction[];
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  shouldShowThreadsMigrationModal?: boolean;
  setOpenModal?: (boolean) => void;
};

const PostFooterButtons = ({
  isLoading,
  singleActions,
  stackedActions,
  onDeleteClick,
  onEditClick,
  shouldShowThreadsMigrationModal,
  setOpenModal,
}: PostFooterButtonsProps): JSX.Element => {
  const { account } = useAccount();
  const commonTrackingProps =
    account?.currentOrganization?.commonTrackingProperties || null;

  const [openInnerModal, setOpenInnerModal] = useState(false);
  const [firstAction, ...restStackedActions] = stackedActions;
  const executeStackedAction = ({ id }) => {
    const selectedAction = stackedActions.find((action) => action.id === id);
    if (selectedAction) selectedAction.callback();
  };

  const closeInnerModal = () => {
    setOpenInnerModal(false);
  };

  return (
    <ButtonWrapper>
      {onDeleteClick && (
        <ConfirmDeleteButton
          onDeleteClick={onDeleteClick}
          isLoading={isLoading}
        />
      )}
      {onEditClick && (
        <Button
          type="text"
          label="Edit"
          size="small"
          disabled={isLoading}
          onClick={onEditClick}
        />
      )}

      {!!singleActions &&
        !shouldShowThreadsMigrationModal &&
        singleActions.map((action) => (
          <Tooltip label={action.tooltip} position="bottom">
            <SingleActionButton
              type="secondary"
              label={action.title}
              size="small"
              onClick={action.callback}
              disabled={isLoading || action.disabled}
              data-testid="single-action-button"
            />
          </Tooltip>
        ))}
      {shouldShowThreadsMigrationModal && (
        <Button
          data-testid="threads-free-paywall-button"
          type="primary"
          size="small"
          icon={<FlashIcon color="white" />}
          label="Upgrade to Schedule Thread"
          onClick={
            setOpenModal
              ? () => {
                  setOpenModal({
                    open: true,
                    ctaString:
                      'calendarDraft-upgradeButton-upgradeToScheduleThread-1',
                  });
                  BufferTracker.ctaClicked({
                    organizationId: account?.currentOrganization?.id || '',
                    ctaName: `publish-calendar-post-upgradeToScheduleThread-1`,
                    cta: `publish-calendar-post-upgradeToScheduleThread-1`,
                    upgradePathName: 'threadsLimit-upgrade',
                    product: 'publish',
                    clientName: 'publishWeb',
                    ...commonTrackingProps,
                  });
                }
              : () => {
                  setOpenInnerModal(true);
                  BufferTracker.ctaClicked({
                    organizationId: account?.currentOrganization?.id || '',
                    ctaName: `publish-draft-post-upgradeToScheduleThread-1`,
                    cta: `publish-draft-post-upgradeToScheduleThread-1`,
                    upgradePathName: 'threadsLimit-upgrade',
                    product: 'publish',
                    clientName: 'publishWeb',
                    ...commonTrackingProps,
                  });
                }
          }
          onOpen={() => {
            BufferTracker.ctaViewed({
              organizationId: account?.currentOrganization?.id || '',
              ctaName: `publish-draft-post-upgradeToScheduleThread-1`,
              upgradePathName: 'threadsLimit-upgrade',
              product: 'publish',
              ...commonTrackingProps,
            });
          }}
        />
      )}
      {firstAction && (
        <StackedActionButton
          size="small"
          type="secondary"
          isSplit={!!restStackedActions}
          items={restStackedActions}
          label={firstAction.title}
          onClick={firstAction.callback}
          onSelectClick={executeStackedAction}
          disabled={isLoading}
        />
      )}
      {openInnerModal && (
        <ThreadsFreeOBPaywall
          closeFirstModal={closeInnerModal}
          ctaString="drafts-upgradeButton-upgradeToScheduleThread-1"
        />
      )}
    </ButtonWrapper>
  );
};

PostFooterButtons.propTypes = {
  isLoading: PropTypes.bool,
  shouldShowThreadsMigrationModal: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  setOpenModal: PropTypes.func,
};

PostFooterButtons.defaultProps = {
  isLoading: false,
  shouldShowThreadsMigrationModal: false,
  onDeleteClick: null,
  onEditClick: null,
  setOpenModal: null,
};

export default PostFooterButtons;
